<script setup>
import { computed } from 'vue'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { flattenProjectsWithLevel } from '@/utils/projectTree'

const props = defineProps({
  projects: {
    type: Array,
    required: true
  },
  modelValue: {
    type: [String, Array],
    default: undefined
  },
  placeholder: {
    type: String,
    default: 'Select a project'
  },
  multiple: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  },
  triggerClass: {
    type: String,
    default: ''
  },
  showAllOption: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue'])

const treeProjects = computed(() => flattenProjectsWithLevel(props.projects || []))

const handleUpdate = (val) => {
  emit('update:modelValue', val)
}

const getProjectName = (projectId) => {
  const project = treeProjects.value.find(p => p.project_id.toString() === projectId.toString())
  return project ? project.project_name : `Project ${projectId}`
}
</script>

<template>
  <Select
    :model-value="modelValue"
    :multiple="multiple"
    :disabled="disabled"
    @update:model-value="handleUpdate"
  >
    <SelectTrigger :class="[multiple ? 'h-auto min-h-[40px] flex-wrap' : '', triggerClass]">
      <slot name="value">
        <SelectValue :placeholder="placeholder">
          <template v-if="multiple && Array.isArray(modelValue) && modelValue.length > 0">
            <div class="flex flex-wrap gap-1">
              <div
                v-for="projectId in modelValue"
                :key="projectId"
                class="bg-primary/10 text-primary px-1.5 py-0.5 rounded-md text-sm flex items-center gap-1"
              >
                {{ getProjectName(projectId) }}
              </div>
            </div>
          </template>
        </SelectValue>
      </slot>
    </SelectTrigger>
    <SelectContent>
      <SelectGroup>
        <SelectItem v-if="showAllOption" value="all">
          All Projects
        </SelectItem>
        <SelectItem
          v-for="project in treeProjects"
          :key="project.project_id"
          :value="project.project_id.toString()"
          :style="{ paddingLeft: `${32 + project._level * 16}px` }"
        >
          {{ project.project_name }}
        </SelectItem>
      </SelectGroup>
    </SelectContent>
  </Select>
</template>
